import { usePriceStore } from '@/store/price-store';
import { cartService } from '@/services/cart.service';
import { PriceFailList, PriceRequest, PriceResponse, PromotionSync } from '@/types/cart';
import { ApplyPromotionFailCode, Response } from '@/types/base';
import { SyncPriceResponse } from '@/types/price';
import { useAppStore } from '@/store/app-store';
import { ChildCatalogs } from '@/types/product-detail';
import { useCartStore } from '../cart/cart-store';
import { useProductStore } from '../cart/product-store';

const priceFetchingMap = new Map();

export const syncPrice = async (childCatalogs: ChildCatalogs[], shipToId?: string, promotion?: PromotionSync) => {
  if (promotion?.promotionCode) {
    await fetchPromotion(
      childCatalogs.map((item) => ({
        catalogNumber: item.childCatalogNumber || item.catalogNumber || '',
        webCatalogNumber: item.webCatalogNumber || item.childCatalogNumber || item.catalogNumber || '',
        quantity: item?.quantity || 1,
      })),
      shipToId,
      promotion
    );
  } else {
    const noPriceCatalogArr = getNoPriceCatalogNumbers(childCatalogs, shipToId);
    const noPriceCatalogNumbers = noPriceCatalogArr[0];
    const noPriceCatalogItems = noPriceCatalogArr[1];
    const noPriceCatalogNumbersStr = JSON.stringify(noPriceCatalogNumbers);
    if (noPriceCatalogNumbers.length > 0 && !priceFetchingMap.get(noPriceCatalogNumbersStr)) {
      priceFetchingMap.set(noPriceCatalogNumbersStr, true);
      const formatNoPriceCatalogItems = noPriceCatalogItems.map((item) => ({
        catalogNumber: item.childCatalogNumber || item.catalogNumber || '',
        webCatalogNumber: item.webCatalogNumber || item.childCatalogNumber || item.catalogNumber || '',
        quantity: item?.quantity || 1,
      }));
      await fetchPrices(formatNoPriceCatalogItems, shipToId);
      priceFetchingMap.delete(noPriceCatalogNumbersStr);
    }
  }

  return generateSyncPriceResponse(childCatalogs);
};

const generateSyncPriceResponse = (childCatalogs: ChildCatalogs[]) => {
  const { productPrice } = usePriceStore.getState();
  const response: SyncPriceResponse = {
    successList: [],
    failList: [],
  };
  childCatalogs.forEach((item) => {
    if (productPrice[item?.childCatalogNumber]) {
      if (productPrice[item?.childCatalogNumber].hasError) {
        response.failList.push(item?.webCatalogNumber || item?.childCatalogNumber || item?.catalogNumber);
      } else {
        response.successList.push(productPrice[item.childCatalogNumber]);
      }
    }
  });
  return response;
};

const getNoPriceCatalogNumbers = (childCatalogs: ChildCatalogs[], shipToId: string) => {
  const { productPrice, priceRefreshDepend, setPriceRefreshDepend, batchRemovePrice } = usePriceStore.getState();
  const shipToChanged = priceRefreshDepend?.shipTo !== shipToId;
  const needRefresh = shipToChanged;

  const filterItemsNeedingPriceUpdate = childCatalogs.filter((item) => {
    return !productPrice[item.childCatalogNumber] || productPrice[item.childCatalogNumber].hasError;
  });

  const filteredCatalogNumbers = filterItemsNeedingPriceUpdate.map((item) => item.childCatalogNumber).sort();

  if (needRefresh) {
    const catalogNumbers = childCatalogs.map((item) => item.childCatalogNumber);
    batchRemovePrice(catalogNumbers);
    setPriceRefreshDepend({
      shipTo: shipToId,
    });

    return [catalogNumbers, childCatalogs];
  } else {
    return [filteredCatalogNumbers, filterItemsNeedingPriceUpdate];
  }
};

const fetchPromotion = async (
  formatNoPriceCatalogItems: PriceRequest[],
  shipToId: string,
  promotion: PromotionSync
) => {
  const billToId = useAppStore.getState()?.billToInfo?.accountNumber;
  const shipTo = shipToId ?? useAppStore.getState()?.shipToList?.find((item) => item.isDefault)?.accountNumber;
  const currentAddress = useCartStore.getState().address;
  const { setPromotionError, setPromotionInfo, batchAddProductPrice, setFBPSkuInfo } = usePriceStore.getState();

  const result = await cartService.getProductPriceByPromoCode(
    formatNoPriceCatalogItems,
    shipTo,
    billToId,
    promotion,
    currentAddress?.addressKey
  );

  if (result.code === '200') {
    const prices = result.data.successList;
    const promotionInfo = result.data.promotionInfo || null;
    if (prices.some((obj) => obj.canApplyPromotion === true)) {
      setPromotionInfo({ ...promotionInfo, isShowPromoCode: true });
    } else {
      if (promotion?.promotionCode) {
        setPromotionInfo({ ...promotionInfo, isShowPromoCode: false });
      } else {
        setPromotionInfo(null);
      }
    }
    batchAddProductPrice(prices);
    handleFailList(result.data?.failList || []);
    if (promotionInfo && promotionInfo.promoType === 'FBP') {
      setFBPSkuInfo(prices);
      const repeatedCatalogNumbers = prices.reduce((acc, price) => {
        acc[price.catalogNumber] = (acc[price.catalogNumber] || 0) + 1;
        return acc;
      }, {});
      const repeatedItems = Object.keys(repeatedCatalogNumbers).filter((key) => repeatedCatalogNumbers[key] > 1);
      const repeatedItemsObject = repeatedItems.reduce((acc, catalogNumber) => {
        const items = prices.filter((price) => price.catalogNumber === catalogNumber);
        const canApplyPromotionTrue = items
          .filter((item) => item.canApplyPromotion)
          .reduce((acc, item) => acc + item.quantity, 0);
        const canApplyPromotionFalse = items
          .filter((item) => !item.canApplyPromotion)
          .reduce((acc, item) => acc + item.quantity, 0);
        acc[catalogNumber] = {
          canApplyPromotionTrue,
          canApplyPromotionFalse,
        };
        return acc;
      }, {});
      const products = useProductStore.getState().products;
      const skuList = products.map((product) => product.catalogNumber);
      const repeatedSkuListItems = skuList.filter((sku, index) => skuList.indexOf(sku) !== index);
      const updatedProducts = [];
      Object.keys(products).forEach((key) => {
        const product = { ...products[key] };
        const repeatedItem = repeatedItemsObject[product.catalogNumber];
        if (repeatedItem && !repeatedSkuListItems.includes(product.catalogNumber)) {
          const { canApplyPromotionTrue, canApplyPromotionFalse } = repeatedItem;
          if (canApplyPromotionTrue > 0) {
            const temp = { ...product, quantity: canApplyPromotionTrue, canApplyPromotion: true };
            updatedProducts.push(temp);
          }
          if (canApplyPromotionFalse > 0) {
            const newProduct = { ...product, quantity: canApplyPromotionFalse, canApplyPromotion: false };
            updatedProducts.push(newProduct);
          }
        } else {
          const temp = { ...product };
          updatedProducts.push(temp);
        }
      });
      useProductStore.getState().initProducts(updatedProducts);
    } else {
      setFBPSkuInfo([]);
    }
  } else {
    setPromotionError(result.code as ApplyPromotionFailCode);
  }
};

const fetchPrices = async (formatNoPriceCatalogItems: PriceRequest[], shipToId: string) => {
  for (let i = 1; i <= Math.ceil(formatNoPriceCatalogItems.length / 10); i++) {
    const batch = formatNoPriceCatalogItems.slice((i - 1) * 10, i * 10);
    const result = await getPrices(batch, shipToId);
    handleFetchResult(result);
  }
};

const getPrices = async (batch: PriceRequest[], shipToId: string) => {
  const billToId = useAppStore.getState()?.billToInfo?.accountNumber;
  const shipTo = shipToId ?? useAppStore.getState()?.shipToList?.find((item) => item.isDefault)?.accountNumber;
  const result = await cartService.getProductPrice(batch, shipTo, billToId);
  if (result.code !== '200') {
    handleFailList(
      batch.map((item) => ({ catalogNumber: item.webCatalogNumber || item.catalogNumber, errorMessage: '' }))
    );
  }
  return result;
};

export const handleFetchResult = (result: Response<PriceResponse>) => {
  const { batchAddProductPrice } = usePriceStore.getState();
  if (result.code === '200') {
    const prices = result.data.successList;

    batchAddProductPrice(prices);
    handleFailList(result.data?.failList || []);
  }
};

const handleFailList = (failList: PriceFailList[]) => {
  if (failList?.length > 0) {
    usePriceStore.getState().batchAddProductPrice(
      failList.map((item) => ({
        catalogNumber: item.catalogNumber,
        listPrice: 0,
        bestPrice: 0,
        itemSubTotal: 0,
        currency: '',
        priceStatus: 'ContactUs',
        quantity: 1,
        hasError: true,
      }))
    );
  }
};

export const removeSplitCart = () => {
  const products = useProductStore.getState().products;
  const combinedProducts = products.reduce((acc, product) => {
    const existingProduct = acc.find((item) => item.catalogNumber === product.catalogNumber);
    if (existingProduct) {
      existingProduct.quantity += product.quantity;
    } else {
      const { canApplyPromotion, ...rest } = product;
      acc.push({ ...rest });
    }
    return acc;
  }, []);
  useProductStore.getState().initProducts(combinedProducts);
};
